/** Renders the slate formatting as rich text, used by TextEditor */
import React from "react";
import { Transforms } from "slate";
import { useSelected, useSlate, useFocused, ReactEditor } from "slate-react";
import { Button, useTheme } from "@mui/material";
import { CustomThemeOptions } from "../../CustomColors/Colors";

// Put this at the start and end of an inline component to work around this Chromium bug:
// https://bugs.chromium.org/p/chromium/issues/detail?id=1249405
const InlineChromiumBugfix = () => (
    <span contentEditable={false} style={{ fontSize: 0 }}>
        ${String.fromCodePoint(160) /* Non-breaking space */}
    </span>
);

/**
 * @param attributes Other formatting already applied to children
 * @param children Selected text to display
 * @param leaf Slate's name for each section of text with identical formatting
 * @returns hyperlink jsx component
 * */
const LinkComponent = ({ attributes, children, element }: any) => {
    const selected = useSelected();
    const theme: CustomThemeOptions = useTheme();
    // if the url does not start with http, add it on
    let url: string = element.url;
    if (url && !url.startsWith("http")) url = `https://${url}`;

    return (
        <a
            {...attributes}
            href={url}
            target="_blank"
            style={{
                boxShadow: selected ? "0 0 0 3px #dd" : "",
                cursor: "pointer",
                color: theme.palette.hyperlink.main,
            }}
        >
            <InlineChromiumBugfix />
            {children}
            <InlineChromiumBugfix />
        </a>
    );
};

/**
 * @param attributes Other formatting already applied to children
 * @param children Selected text to display
 * @param leaf Slate's name for each section of text with identical formatting
 * @returns image jsx component
 * */
const Image = ({ attributes, children, element }: any) => {
    const editor = useSlate();
    const path = ReactEditor.findPath(editor, element);
    const selected = useSelected();
    const focused = useFocused();
    return (
        <div {...attributes}>
            {children}
            <div contentEditable={false} style={{ position: "relative" }}>
                <img
                    src={element.url}
                    style={{
                        display: "block",
                        maxWidth: "100%",
                        maxHeight: "20em",
                        boxShadow:
                            selected && focused ? "0 0 0 3px #B4D5FF" : "none",
                    }}
                />
                <Button
                    // active
                    onClick={() => Transforms.removeNodes(editor, { at: path })}
                    style={{
                        display: selected && focused ? "inline" : "none",
                        position: "absolute",
                        top: "0.5em",
                        left: "0.5em",
                        backgroundColor: "white",
                    }}
                >
                    delete
                </Button>
            </div>
        </div>
    );
};

/**
 * @param attributes Other formatting already applied to children
 * @param children Selected text to display
 * @param code Boolean flag to check if it is code tag
 * @param codeBlock Boolean flag to check if it is code block tag
 * @returns paragraph component that wraps children with appropriate HTML block tags fitting for their formatting
 */
const renderParagraph = (
    attributes: any,
    children: any,
    code: boolean = false,
    codeBlock: boolean = false
) => {
    const style =
        code || codeBlock
            ? {
                  marginTop: 0,
                  marginBottom: 0,
                  backgroundColor: codeBlock ? "lightgrey" : "none",
              }
            : {};
    return (
        <p style={style} {...attributes}>
            {children}
        </p>
    );
};

/** Wraps children with appropriate HTML block tags fitting for their formatting
 * (each block can only have one kind of block formatting)
 * @param attributes Other formatting already applied to children
 * @param children Selected text to display
 * @param leaf Slate's name for each section of text with identical formatting */
export const Element = (props: any) => {
    const { attributes, children, element } = props;
    switch (element.type) {
        case "heading-one":
            return <h1 {...attributes}>{children}</h1>;
        case "heading-two":
            return <h2 {...attributes}>{children}</h2>;
        case "block-quote":
            return <blockquote {...attributes}>{children}</blockquote>;
        case "list-item":
            return <li {...attributes}>{children}</li>;
        case "numbered-list":
            return <ol {...attributes}>{children}</ol>;
        case "bulleted-list":
            return <ul {...attributes}>{children}</ul>;
        case "link":
            return <LinkComponent {...props} />;
        case "image":
            return <Image {...props} />;
        default:
            if (children.props.node.children[0].code) {
                return renderParagraph(attributes, children, true);
            } else if (children.props.node.children[0].codeBlock) {
                return renderParagraph(attributes, children, false, true);
            }
            return renderParagraph(attributes, children);
    }
};

/**
 * @param children Selected text to display
 * @param codeBlock Boolean flag to check if it is code block tag
 * @returns code html tag that wraps children
 */
const renderCodeTag = (children: any, codeBlock: boolean = false) => {
    return (
        <code
            style={{
                paddingLeft: "10px",
                paddingRight: "10px",
                fontFamily:
                    "Monaco,Menlo,Consolas,Courier New,monospace!important",
                backgroundColor: "lightgrey",
                border: codeBlock ? "none" : "1px solid darkgrey",
                color: "#5034FF",
                lineHeight: 1.2,
            }}
        >
            {children}
        </code>
    );
};

/** Wraps children with appropriate HTML font tags
 * (note that font tags are NOT mutually exclusive, and all can be applied at once)
 * @param attributes Other formatting already applied to children
 * @param children Selected text to display
 * @param leaf Slate's name for each section of text with identical formatting */
export const Leaf = ({ attributes, children, leaf }: any) => {
    if (leaf.bold) children = <strong>{children}</strong>;
    if (leaf.codeBlock) children = renderCodeTag(children, true);
    if (leaf.code) children = renderCodeTag(children);
    if (leaf.italic) children = <em>{children}</em>;
    if (leaf.underline) children = <u>{children}</u>;
    if (leaf.strikethrough) children = <del>{children}</del>;
    if (leaf.highlight)
        children = (
            <mark style={{ backgroundColor: "yellow" }}>{children}</mark>
        );

    return (
        <span
            {...attributes}
            style={leaf.color === "black" ? {} : { color: leaf.color }}
        >
            {children}
        </span>
    );
};
